// DEPENDENCIES ---------------------------------------------------------------- //

import React from 'react';
import { Smsmessages } from '@gql/base-types';
import { useGetSmsmessagesQuery } from '../queries/getSmsmessages.query.generated';
import { useValetSelfManaged } from '@hopdrive/feature-portal-products';

// TYPES ---------------------------------------------------------------- //

interface UseMoveChat {
  messages: Smsmessages[];
  messageCount: number;
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

interface UseMoveChatsOptions {
  enabled?: boolean;
  pollInterval?: number;
}

// HOOK ---------------------------------------------------------------- //

export const useMoveChat = (
  moveId?: number, 
  consumerPickup?: boolean,
  options: UseMoveChatsOptions = {}
): UseMoveChat => {
  const { enabled = true, pollInterval } = options;

  // The working rooftop's Valet is self-managed-capable → SMS features are available.
  const isSmsEnabled = useValetSelfManaged();

  const shouldFetch = moveId && consumerPickup && enabled && isSmsEnabled;

  // Use cache-first for immediate rendering with streaming updates
  // Only use polling if explicitly provided (for backward compatibility)
  const { data, loading, error, refetch } = useGetSmsmessagesQuery({
    variables: { moveId: moveId! },
    skip: !shouldFetch,
    pollInterval: pollInterval, // Only poll if explicitly requested
    fetchPolicy: 'cache-first', // Changed from 'no-cache' for streaming pattern
    errorPolicy: 'all',
  });

  const messages = React.useMemo(() => {
    // Return empty array if SMS not enabled
    if (!isSmsEnabled) {
      return [];
    }
    const msgs = (data?.smsmessages as Smsmessages[]) || [];
    return msgs;
  }, [data, isSmsEnabled]);

  const messageCount = messages.length;

  return React.useMemo(() => ({
    messages,
    messageCount,
    loading: isSmsEnabled ? (loading && !data) : false,
    error: isSmsEnabled ? !!error : false,
    refetch: isSmsEnabled ? refetch : () => {},
  }), [messages, messageCount, loading, data, error, refetch, isSmsEnabled]);
};

export default useMoveChat;